// "Already listed" suggestions for the add-a-skill and add-a-role-type forms:
// existing names close to what someone is about to add, so "ReactJS" finds
// "React" before a near-duplicate goes to review. Picker UI only; ranking
// never uses it (rule 1).

/** Lowercase, accents and punctuation dropped; `+`/`#` kept so C, C++ and C# differ. */
function normalize(name: string) {
  const bare = name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9+#]+/g, '');
  // "Node.js", "NodeJS" and "Node" are one technology.
  return bare.length > 4 && bare.endsWith('js') ? bare.slice(0, -2) : bare;
}

/** Same words in any order: "Forecasting revenue" is "Revenue forecasting". */
const wordKey = (name: string) => name.split(/\s+/).map(normalize).filter(Boolean).sort().join(' ');

/** Levenshtein distance, giving up (returning `max + 1`) once it exceeds `max`. */
function editDistance(a: string, b: string, max: number) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      const swap = a[i - 1] === b[j - 1] ? 0 : 1;
      row.push(Math.min(prev[j]! + 1, row[j - 1]! + 1, prev[j - 1]! + swap));
    }
    if (Math.min(...row) > max) return max + 1;
    prev = row;
  }
  return prev[b.length]!;
}

/**
 * Existing items whose name is close to `typed`, closest first: same name once
 * normalized or same words in any order, then one name containing the other
 * (3+ characters), then a typo or two (one edit for short names). Empty for
 * fewer than two meaningful characters.
 */
export function similarNames<T extends { name: string }>(
  typed: string,
  items: readonly T[],
  limit = 5,
): T[] {
  const q = normalize(typed);
  if (q.length < 2) return [];
  const qWords = wordKey(typed);
  const maxEdits = q.length <= 5 ? 1 : 2;
  const scored: { item: T; score: number }[] = [];
  for (const item of items) {
    const n = normalize(item.name);
    if (!n) continue;
    let score: number | null = null;
    if (n === q || wordKey(item.name) === qWords) score = 0;
    else if (Math.min(n.length, q.length) >= 3 && (n.includes(q) || q.includes(n))) {
      // Closer lengths first: "React" before "React Native" for "ReactJS".
      score = 1 + Math.abs(n.length - q.length) / 100;
    } else {
      const d = editDistance(q, n, maxEdits);
      if (d <= maxEdits) score = 2 + d;
    }
    if (score !== null) scored.push({ item, score });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.item.name.localeCompare(b.item.name))
    .slice(0, limit)
    .map((s) => s.item);
}
